import { isTalkBoardOpen } from '../talk/talkOpen';
import { playCustom, stopCustom } from '../media/media';
import { token as liveToken } from '../auth/session';
import { spokenText } from './phrases';

type SpeakOptions = {
  voiceName?: string | null;
  rate?: number;
  pitch?: number;
  fromTalkBoard?: boolean;
};

export const SERVER_VOICES = ['gb-c', 'gb-a', 'gb-b', 'studio-c', 'aoede', 'achernar', 'emma', 'alice', 'george', 'heart', 'cori', 'alba', 'northern'] as const;
export const DEFAULT_SERVER_VOICE = 'gb-c';
export const DEVICE_DEFAULT = 'device';

const WAIT_STEP_MS = 800;
const WAIT_LIMIT_MS = 60_000;

export function canSpeak(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

export function serverVoice(voiceName: string | null | undefined): string | null {
  if (!voiceName) return DEFAULT_SERVER_VOICE;
  if (voiceName.startsWith('el:')) {
    const id = voiceName.slice(3);
    return (SERVER_VOICES as readonly string[]).includes(id) ? id : null;
  }
  return null;
}

const clips = new Map<string, string>();
let current: HTMLAudioElement | null = null;
let generation = 0;
let deviceForSession = false;

export function setDeviceVoiceForSession(on: boolean) {
  deviceForSession = on;
}

const stepRate = (rate: number | undefined) => Math.round((rate ?? 0.9) * 20) / 20;
const keyOf = (voice: string, rate: number, text: string) => `${voice}|${rate}|${spokenText(text).toLowerCase()}`;

function remember(key: string, url: string) {
  clips.set(key, url);
  if (clips.size > 600) {
    const oldest = clips.keys().next().value as string;
    URL.revokeObjectURL(clips.get(oldest) as string);
    clips.delete(oldest);
  }
}

async function fetchClip(voice: string, rate: number, text: string): Promise<'ready' | 'waiting' | 'failed'> {
  const key = keyOf(voice, rate, text);
  if (clips.has(key)) return 'ready';
  try {
    const bearer = await liveToken();
    if (!bearer) return 'failed';
    const res = await fetch(`/api/speech?v=${voice}&r=${rate}&t=${encodeURIComponent(text)}`, {
      headers: { Authorization: `Bearer ${bearer}` },
      credentials: 'omit',
    });
    if (res.status === 202) return 'waiting';
    if (res.status !== 200) return 'failed';
    remember(key, URL.createObjectURL(await res.blob()));
    return 'ready';
  } catch {
    return 'waiting';
  }
}

const pause = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function waitForClip(voice: string, rate: number, text: string, stillWanted: () => boolean): Promise<string | null> {
  const started = Date.now();
  while (stillWanted() && Date.now() - started < WAIT_LIMIT_MS) {
    const state = await fetchClip(voice, rate, text);
    if (state === 'ready') return clips.get(keyOf(voice, rate, text)) ?? null;
    if (state === 'failed') return null;
    await pause(WAIT_STEP_MS);
  }
  return null;
}

function play(url: string, mine: number): Promise<void> {
  return new Promise((done) => {
    if (mine !== generation) { done(); return; }
    const el = new Audio(url);
    current = el;
    el.onended = () => done();
    el.onerror = () => done();
    void el.play().catch(() => done());
  });
}

function deviceSpeak(text: string, opts: SpeakOptions, named: boolean): Promise<void> {
  if (!canSpeak()) return Promise.resolve();
  return new Promise((resolve) => {
    const synth = window.speechSynthesis;
    synth.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = opts.rate ?? 0.9;
    utter.pitch = opts.pitch ?? 1.0;
    utter.volume = 1.0;
    if (named && opts.voiceName) {
      const v = synth.getVoices().find((vv) => vv.name === opts.voiceName);
      if (v) utter.voice = v;
    }
    utter.onend = () => resolve();
    utter.onerror = () => resolve();
    synth.speak(utter);
  });
}

export async function speak(text: string, opts: SpeakOptions = {}): Promise<void> {
  if (!opts.fromTalkBoard && isTalkBoardOpen()) return;
  stopSpeaking();
  const mine = generation;
  const custom = playCustom(text);
  if (custom) return custom;
  const voice = serverVoice(opts.voiceName);
  if (voice && !deviceForSession) {
    const rate = stepRate(opts.rate);
    const url = clips.get(keyOf(voice, rate, text)) ?? await waitForClip(voice, rate, text, () => mine === generation);
    if (url && mine === generation) return play(url, mine);
    return;
  }
  return deviceSpeak(text, opts, !voice && opts.voiceName !== DEVICE_DEFAULT);
}

export function stopSpeaking(): void {
  generation++;
  stopCustom();
  if (current) { current.pause(); current = null; }
  if (canSpeak()) window.speechSynthesis.cancel();
}

async function postPrepare(texts: string[], voice: string | null, rate: number | undefined, urgent: boolean) {
  const list = [...new Set(texts.filter((t) => /\p{L}/u.test(t)))].slice(0, 300);
  if (!list.length) return list;
  const bearer = await liveToken();
  if (!bearer) return list;
  await fetch('/api/speech/prepare', {
    method: 'POST',
    headers: { Authorization: `Bearer ${bearer}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ texts: list, voice, rate: rate === undefined ? null : stepRate(rate), urgent }),
    credentials: 'omit',
  });
  return list;
}

export async function prepareSpeech(texts: string[], voiceName?: string | null, rate?: number): Promise<void> {
  try {
    await postPrepare(texts, voiceName === undefined ? null : serverVoice(voiceName), rate, false);
  } catch {
    return;
  }
}

export async function readySpeech(
  texts: string[],
  voiceName: string | null | undefined,
  rateSetting: number | undefined,
  onProgress: (ready: number, total: number) => void,
  stillWanted: () => boolean,
): Promise<boolean> {
  const voice = serverVoice(voiceName);
  if (!voice) return true;
  const rate = stepRate(rateSetting);
  let list: string[];
  try {
    list = await postPrepare(texts, voice, rate, true);
  } catch {
    list = [...new Set(texts.filter((t) => /\p{L}/u.test(t)))].slice(0, 300);
  }
  let ready = 0;
  onProgress(0, list.length);
  const queue = [...list];
  const workers = Array.from({ length: Math.min(4, queue.length) }, async () => {
    for (let text = queue.shift(); text !== undefined && stillWanted(); text = queue.shift()) {
      while (stillWanted()) {
        const state = await fetchClip(voice, rate, text);
        if (state !== 'waiting') break;
        await pause(WAIT_STEP_MS);
      }
      ready++;
      if (stillWanted()) onProgress(ready, list.length);
    }
  });
  await Promise.all(workers);
  return stillWanted() && list.every((t) => clips.has(keyOf(voice, rate, t)));
}
